import { create } from 'zustand'
import { HSVA } from '@/features/mobile-app/store/useColorStore'
import { ShapeType } from '@/shared/api/BaseAPI.model'

type Alignment = 'left' | 'center'

type InitialStateType = {
  id: string | undefined
  tableNumber: number
  /** Backend table id of the previewed table, the QR encodes this. */
  tableId: string | undefined
  shapeValue: ShapeType
  backgroundColor: HSVA
  contentColor: HSVA
  contrastColor: HSVA
  withNfc: boolean
  languageCode: 'en' | 'pl'
  alignment: Alignment
  backgroundImage: string | null
  widthValue: number
  heightValue: number
  diameterValue: number
  title: string
  description: string
  tableIds: string[]
}

const initialState: InitialStateType = {
  id: undefined,
  tableNumber: 73,
  tableId: undefined,
  shapeValue: 'horizontalRectangle',
  backgroundColor: { h: 24, s: 100, v: 100, a: 1 },
  contentColor: { h: 0, s: 0, v: 100, a: 1 },
  contrastColor: { h: 0, s: 0, v: 0, a: 0 },
  withNfc: false,
  languageCode: 'en',
  alignment: 'left',
  backgroundImage: null,
  widthValue: 85,
  heightValue: 55,
  diameterValue: 50,
  title: '',
  description: '',
  tableIds: []
}

interface TableCardState extends InitialStateType {
  baseline: InitialStateType
  setBaseline: (data: InitialStateType) => void
  checkIsDirty: () => boolean
  resetToBaseline: () => void
  resetToInitialState: () => void
  setId: (id: string) => void
  setTableNumber: (number: number) => void
  setTableId: (id: string | undefined) => void
  setShapeValue: (value: ShapeType) => void
  setBackgroundColor: (color: HSVA) => void
  setContentColor: (color: HSVA) => void
  setContrastColor: (color: HSVA) => void
  setWithNfc: (value: boolean) => void
  setLanguageCode: (value: 'en' | 'pl') => void
  setAlignment: (value: Alignment) => void
  setBackgroundImage: (url: string | null) => void
  setWidthValue: (value: number) => void
  setHeightValue: (value: number) => void
  setDiameterValue: (value: number) => void
  setTableIds: (ids: string[]) => void
  setTitle: (title: string) => void
  setDescription: (description: string) => void
  reset: () => void
}

export const useTableCardStore = create<TableCardState>((set, get) => ({
  ...initialState,

  baseline: initialState,

  setBaseline: (data: InitialStateType) => set({ baseline: data }),

  checkIsDirty: () => {
    const state = get()
    const base = state.baseline

    return Object.entries(base).some(([key, value]) => {
      const k = key as keyof InitialStateType
      return JSON.stringify(state[k]) !== JSON.stringify(value)
    })
  },

  resetToBaseline: () => {
    const base = get().baseline
    set(base)
  },

  resetToInitialState: () =>
    set({
      ...initialState,
      baseline: initialState
    }),
  setId: id => set({ id }),
  setTableNumber: number => set({ tableNumber: number }),
  setTableId: tableId => set({ tableId }),
  setShapeValue: value => set({ shapeValue: value }),
  setBackgroundColor: color => set({ backgroundColor: color }),
  setContentColor: color => set({ contentColor: color }),
  setContrastColor: color => set({ contrastColor: color }),
  setWithNfc: value => set({ withNfc: value }),
  setLanguageCode: value => set({ languageCode: value }),
  setAlignment: value => set({ alignment: value }),
  setBackgroundImage: url => set({ backgroundImage: url }),
  setWidthValue: value => set({ widthValue: value }),
  setHeightValue: value => set({ heightValue: value }),
  setDiameterValue: value => set({ diameterValue: value }),
  setTableIds: ids => set({ tableIds: ids }),
  setTitle: title => set({ title }),
  setDescription: description => set({ description }),
  reset: () => set(initialState)
}))
